import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Target, Zap, Award, CheckCircle2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ACHIEVEMENTS_DATA } from '@/data/achievements-data';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  unlocked: boolean;
  unlockedAt?: Date;
  progress?: number;
  maxProgress?: number;
}

interface AchievementContextType {
  achievements: Achievement[];
  unlockAchievement: (id: string) => void;
  updateProgress: (id: string, progress: number) => void;
  showToast: (achievement: Achievement) => void;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within AchievementProvider');
  }
  return context;
}

function AchievementToast({ achievement, onClose }: { achievement: Achievement; onClose: () => void }) {
  const Icon = achievement.icon;

  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.9 }}
      className="fixed bottom-20 right-8 z-9999 max-w-sm"
    >
      <div className="bg-card border-2 border-primary rounded-xl p-4 shadow-2xl shadow-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">
                Achievement Unlocked!
              </span>
            </div>
            <div className="font-bold text-foreground mb-1" role="heading" aria-level={2}>{achievement.title}</div>
            <p className="text-sm text-muted-foreground">{achievement.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            ×
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function AchievementProvider({ children }: { children: ReactNode }) {
  const [achievements, setAchievements] = useState<Achievement[]>(() => {
    const saved = localStorage.getItem('achievements');
    if (saved) {
      const parsed = JSON.parse(saved);
      return ACHIEVEMENTS_DATA.map(a => {
        const savedData = parsed.find((s: Achievement) => s.id === a.id);
        return {
          ...a,
          unlocked: savedData?.unlocked || false,
          unlockedAt: savedData?.unlockedAt ? new Date(savedData.unlockedAt) : undefined,
          progress: savedData?.progress || a.progress || 0,
        };
      });
    }
    return ACHIEVEMENTS_DATA.map(a => ({ ...a, unlocked: false, progress: a.progress || 0 }));
  });

  const [toastAchievement, setToastAchievement] = useState<Achievement | null>(null);

  useEffect(() => {
    localStorage.setItem('achievements', JSON.stringify(achievements));
  }, [achievements]);

  const unlockAchievement = (id: string) => {
    setAchievements(prev => {
      const achievement = prev.find(a => a.id === id);
      if (!achievement || achievement.unlocked) return prev;

      const updated = prev.map(a =>
        a.id === id
          ? { ...a, unlocked: true, unlockedAt: new Date() }
          : a
      );

      // Show toast
      const unlockedAchievement = updated.find(a => a.id === id);
      if (unlockedAchievement) {
        setToastAchievement(unlockedAchievement);
      }

      return updated;
    });
  };

  const updateProgress = (id: string, progress: number) => {
    setAchievements(prev => {
      const achievement = prev.find(a => a.id === id);
      if (!achievement || achievement.unlocked) return prev;

      const updated = prev.map(a => {
        if (a.id === id) {
          const newProgress = Math.min(progress, a.maxProgress || 0);
          const shouldUnlock = newProgress >= (a.maxProgress || 0);
          
          if (shouldUnlock) {
            // Show toast
            setToastAchievement({ ...a, unlocked: true });
            return { ...a, progress: newProgress, unlocked: true, unlockedAt: new Date() };
          }
          
          return { ...a, progress: newProgress };
        }
        return a;
      });

      return updated;
    });
  };

  const showToast = (achievement: Achievement) => {
    setToastAchievement(achievement);
  };

  return (
    <AchievementContext.Provider value={{ achievements, unlockAchievement, updateProgress, showToast }}>
      {children}
      <AnimatePresence>
        {toastAchievement && (
          <AchievementToast
            achievement={toastAchievement}
            onClose={() => setToastAchievement(null)}
          />
        )}
      </AnimatePresence>
    </AchievementContext.Provider>
  );
}

// Achievements Display Component
export function AchievementsApp() {
  const { achievements } = useAchievements();
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;

  return (
    <div className="h-full overflow-auto bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">Achievements</h2>
            <p className="text-muted-foreground">
              {unlockedCount} of {totalCount} unlocked ({Math.round((unlockedCount / totalCount) * 100)}%)
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 p-4 bg-card rounded-xl border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">{unlockedCount}/{totalCount}</span>
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-linear-to-r from-primary to-primary/80"
              initial={{ width: 0 }}
              animate={{ width: `${(unlockedCount / totalCount) * 100}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Achievements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements.map((achievement) => {
            const Icon = achievement.icon;
            const hasProgress = achievement.maxProgress !== undefined;
            const progress = achievement.progress || 0;
            const maxProgress = achievement.maxProgress || 0;

            return (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  'p-6 rounded-xl border-2 transition-all',
                  achievement.unlocked
                    ? 'bg-card border-primary shadow-lg shadow-primary/10'
                    : 'bg-card/50 border-border opacity-60'
                )}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center shrink-0',
                      achievement.unlocked
                        ? 'bg-primary/20 text-primary'
                        : 'bg-secondary text-muted-foreground'
                    )}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold mb-1 flex items-center gap-2" role="heading" aria-level={3}>
                      {achievement.title}
                      {achievement.unlocked && (
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {achievement.description}
                    </p>
                    {hasProgress && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">
                            {progress}/{maxProgress}
                          </span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-primary"
                            initial={{ width: 0 }}
                            animate={{ width: `${(progress / maxProgress) * 100}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>
                    )}
                    {achievement.unlocked && achievement.unlockedAt && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
